<template>
  <div v-if="projectStore.selectedProject" class="import-page">
    <div class="layout__content__header layout__content__header--white">
      <Back :route="`/devices/lora/${$route.params.customerId}/${$route.params.projectId}`" :text="'zpet'" />
      <h1 class="text-center mb-2 mb-md-3">
        {{ $t("DEVICE_IMPORT") }}
      </h1>
    </div>
    <div class="layout__content__content layout__content__content--white">
      <div class="import-form text-left">
        <div ref="errors" class="import-form__wrapper justify-content-center mt-3 mx-auto">
          <div v-show="containsError" class="text-danger">
            {{ $t("VyplnteVsechnaPovinnaPole") }}
          </div>
          <div v-show="containsPasswordError" class="text-danger">
            {{ $t("PASSWORDS_NOT_MATCH_ERROR") }}
          </div>
        </div>

        <div class="import-form__wrapper justify-content-center mt-3 mx-auto">
          <FormInput
            :has-error="containsError && !device.custDeviceName"
            :label="$t('nazevZarizeni')"
            :value="device.custDeviceName"
            class="mb-4"
            info-text="IMPORT_DEVICE_NAME_INFO"
            info-tooltip="IMPORT_DEVICE_NAME_TOOLTIP"
            @input="device.custDeviceName = $event"
          />
          <FormSelect
            :custom-action-text="$t('CREATE_NEW_DATA_FLOW')"
            :has-error="containsError && !device.dataFlowId"
            :items="dataFlowList"
            :label="$t('DATA_FLOW')"
            :placeholder="$t('IMPORT_DEVICE_DATA_FLOW_SELECT_PLACEHOLDER')"
            :value="device.dataFlow"
            class="mb-4"
            item-subtext="technologyDisplay"
            item-text="deviceGroupName"
            item-value="deviceGroupId"
            @change="handleDataFlowChanged"
            @custom-action-clicked="isLeaveConfirmOpen = true"
          />
          <div v-if="device.dataFlow && !technology" class="box-danger">
            {{ $t("NO_GROUP_TECHNOLOGY") }}
          </div>

          <NewDeviceFormFields :contains-error="containsError" :device="device" :technology="technology" />

          <div v-if="technology" class="mt-3 text-right">
            <button
              :class="{
                'primary-shadow': ['LORA', 'MQTT', 'UDP'].includes(technology)
              }"
              :disabled="!['LORA', 'MQTT', 'UDP'].includes(technology)"
              class="btn btn-primary"
              @click="validateInputs"
            >
              {{ $t("ADD_DEVICE") }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ConfirmLeave :is-open="isLeaveConfirmOpen" @cancel="handleCloseLeaveConfirmation" @confirm="handleOpenNewTab" />
    <ErrorModal :centered="true" :errors="errorList" :opened="showErrorModal" @close="showErrorModal = false" />
  </div>
</template>

<script>
import axios from "axios"

import Back from "@/components/back.vue"
import ErrorModal from "@/components/confirmations/error.vue"

import NewDeviceFormFields from "@/components/devices/partials/NewDeviceFormFields.vue"
import FormInput from "@/components/forms/FormInput.vue"
import FormSelect from "@/components/forms/FormSelect.vue"
import ConfirmLeave from "@/components/import/ConfirmLeave.vue"

import { mapState } from "vuex"
import { useCustomerStore } from "@/store/modules/customer"
import { useProjectStore } from "@/store/modules/project"
import { useAuthStore } from "@/store/modules/auth"

export default {
  components: {
    Back,
    ConfirmLeave,
    ErrorModal,
    FormInput,
    FormSelect,
    NewDeviceFormFields
  },
  setup() {
    const customerStore = useCustomerStore()
    const projectStore = useProjectStore()
    const authStore = useAuthStore()

    return { customerStore, projectStore, authStore }
  },
  data: () => ({
    device: {
      deviceId: "",

      // LORA
      devicetype: "", // will be set on NewDeviceFormFields.mounted
      selectedType: {}, // will be set on NewDeviceFormFields.mounted

      appeui: "",
      appKey: "",
      appSKey: "",
      nwkSKey: "",
      devclass: "A",
      devAddr: "",

      // LORA admin fields
      deviceAdrSelector: null, // helper field
      adrFix: null,
      adrMax: null,
      adrMin: null,

      // MQTT
      username: "",
      password: "",
      confirmPassword: "",

      // Common
      custDeviceName: "",
      custDeviceDesc: "",
      custServiceId: "",
      dataFlow: null,
      dataFlowId: null,
      projectId: "",
      selectedService: {},
      selectedState: {}, // will be set on NewDeviceFormFields.mounted
      tags: [],
      transformationId: null
    },

    dataFlowList: [],

    containsError: false,
    containsPasswordError: false,

    errorList: [],
    isLeaveConfirmOpen: false,
    showErrorModal: false,
    technology: ""
  }),
  mounted() {
    this.loadDataFlows()
  },
  methods: {
    async afterImportCalls() {
      let response
      // Assign to data flow
      response = await axios.post(
        `${process.env.VUE_APP_API}/device-groups/${this.device.dataFlowId}/devices/${this.technology.toLowerCase()}/${
          this.device.deviceId
        }/assign`
      )
      if (response.data.status === "error") {
        this.setErrors(response.data.errors)
      }

      // Assign transformationId (got from data flow hidden tag)
      if (this.device.transformationId) {
        response = await axios.put(
          `${process.env.VUE_APP_API}/${this.technology.toLowerCase()}/devices/${this.device.deviceId}`,
          {
            custDeviceName: this.device.custDeviceName,
            transformationId: this.device.transformationId
          }
        )
        if (response.data.status === "error") {
          this.setErrors(response.data.errors)
        }
      }

      // Add tags
      response = await axios.put(
        `${process.env.VUE_APP_API}/${this.technology.toLowerCase()}/devices/${this.device.deviceId}/tags`,
        this.device.tags.map((t) => t.value)
      )
      if (response.data.status === "error") {
        this.setErrors(response.data.errors)
      }

      // Assign to service
      // response = await axios.post(
      //     `${process.env.VUE_APP_API}/services/${this.device.custServiceId}/devices/${this.technology.toLowerCase()}/${this.device.deviceId}/assign`,
      //     this.device.tags.map(t => t.value)
      // )
      // if (response.data.status === 'error') {
      //   this.setErrors(response.data.errors)
      // }

      // Set status
      const stateMethod =
        this.device.selectedState.value === "A" ? "post" : this.device.selectedState.value === "S" ? "delete" : ""
      if (stateMethod) {
        response = await axios[stateMethod](
          `${process.env.VUE_APP_API}/${this.technology.toLowerCase()}/devices/${this.device.deviceId}/enable`
        )
        if (response.data.status === "error") {
          this.setErrors(response.data.errors)
        }
      }

      // Redirect finally to device detail
      this.$router.push({
        name: this.technology === "LORA" ? "device" : this.technology === "MQTT" ? "mqttDevice" : "udpDevice",
        params: {
          id: this.device.deviceId,
          customerId: this.$route.params.customerId,
          projectId: this.$route.params.projectId
        }
      })
    },
    async checkImportStatus(uniqueId) {
      const response = await axios.post(
        `${process.env.VUE_APP_APICMS}/checkImport?uniqueid=${uniqueId}&single=1&technology=${this.technology}`,
        {
          token: this.authStore.user.access_token
        }
      )

      if (response.data.summary.complete === 1) {
        await this.afterImportCalls()
        return
      } else if (response.data.summary.error === 1 || response.data.error?.messages?.length) {
        // const status_response = await axios.get(`${process.env.VUE_APP_API}/${this.technology.toLowerCase()}/devices/import/${response.data.uniqueid}`)
        if (response.data.summary.errorList) {
          this.setErrors(Object.values(response.data.summary.errorList).map((e) => e.errorMessage))
        } else {
          this.setErrors(response.data.error?.messages || [])
        }
        return
      }

      await this.checkImportStatus(uniqueId)
    },
    async handleAddDevice() {
      this.$store.commit("modals/setWaitingLoader", this.$t("Ukládám data, čekejte prosím..."))
      let dataToImport = JSON.parse(JSON.stringify(this.device))

      // Set special data fields
      dataToImport.projectId = this.$route.params.projectId
      dataToImport.technology = this.technology
      dataToImport.token = this.authStore.user.access_token

      // -- Service data
      // dataToImport.custServiceId = this.device.selectedService.custServiceId; done in change handler
      const DEV_LMT_ACTUAL = this.device.selectedService.parameters.find((e) => e.name === "COUNTER-DEV_LMT_ACTUAL")

      if (DEV_LMT_ACTUAL) {
        dataToImport.devicesLimit = DEV_LMT_ACTUAL.value - this.device.selectedService.devices.count
        dataToImport.unlimited = parseInt(DEV_LMT_ACTUAL.value) === 0 ? 1 : 0 // if DEV_LMT_ACTUAL is 0 there is no limit
      } else {
        // if there is no DEV_LMT_ACTUAL parameter, there is no limit
        dataToImport.devicesLimit = 0
        dataToImport.unlimited = 1
      }

      dataToImport.preregistration = this.device.selectedState.value === "P"

      // Clear some helper fields
      dataToImport.dataFlow = undefined
      dataToImport.deviceAdrSelector = undefined
      dataToImport.selectedService = undefined
      dataToImport.selectedState = undefined
      dataToImport.selectedType = undefined
      dataToImport.tags = undefined

      // Perform actual request
      const response = await axios.post(`${process.env.VUE_APP_APICMS}/processImportFile`, dataToImport)

      // Now we need to recursively check for status to change to success or error
      if (response.data.status === "dataReceived") {
        await this.checkImportStatus(response.data.uniqueid)
      } else {
        this.errorList = response.data.error?.[0]?.messages || []
        this.showErrorModal = true
      }

      this.$store.commit("modals/setWaitingLoader", "")
    },
    handleCloseLeaveConfirmation() {
      this.loadDataFlows()
      this.isLeaveConfirmOpen = false
    },
    handleDataFlowChanged(dataFlow) {
      this.device.dataFlow = dataFlow
      this.device.dataFlowId = dataFlow.deviceGroupId
      this.technology = dataFlow.technology

      const tfIdStr = (this.device.transformationId =
        dataFlow.tags.find((t) => t.startsWith("hidden_tf_"))?.substring(10) || "")
      if (tfIdStr) {
        this.device.transformationId = parseInt(tfIdStr)
      }

      // TODO (?) might be useful to reset technology specific fields (or not)
      this.device.custServiceId = ""
    },
    handleOpenNewTab() {
      const dataFlowWindow = open(
        this.$router.resolve({
          name: "dataFlowNew",
          params: this.$route.params
        }).href,
        "_blank"
      )
      dataFlowWindow.onbeforeunload = this.handleCloseLeaveConfirmation
    },
    async loadDataFlows() {
      const response = await axios.get(
        `${process.env.VUE_APP_API}/device-groups?projectId[eq]=${this.$route.params.projectId}&limit=1000&sort=%2BdeviceGroupName`
      )
      this.dataFlowList = response.data.data.map((df) => ({
        ...df,
        technologyDisplay: df.technology === "LORA" ? this.$t("lora") : this.$t(df.technology ?? "")
      }))
    },
    validateInputs() {
      if (this.technology === "LORA") {
        this.validateInputsLora()
      } else if (this.technology === "MQTT") {
        this.validateInputsMqtt()
      } else if (this.technology === "UDP") {
        this.validateInputsUdp()
      } else {
        this.handleAddDevice()
      }
    },
    validateInputsLora() {
      this.containsError = false
      let isValid = false

      if (this.device.devicetype === "ABP") {
        isValid =
          !!this.device.nwkSKey &&
          !!this.device.devAddr &&
          !!this.device.deviceId &&
          !!this.device.custDeviceName &&
          !!this.device.custServiceId &&
          !!this.device.profileId
      } else if (this.device.devicetype === "OTAA") {
        isValid =
          !!this.device.appKey &&
          !!this.device.appeui &&
          !!this.device.deviceId &&
          !!this.device.custDeviceName &&
          !!this.device.custServiceId &&
          !!this.device.profileId
      }

      if (isValid) {
        this.handleAddDevice()
      } else {
        this.containsError = true
        this.$refs.errors.scrollIntoView()
      }
    },
    validateInputsMqtt() {
      this.containsError = false
      this.containsPasswordError = false

      if (this.device.password !== this.device.confirmPassword) {
        this.containsPasswordError = true
        this.$refs.errors.scrollIntoView()
        return
      }

      const isValid =
        !!this.device.deviceId &&
        !!this.device.username &&
        !!this.device.password &&
        !!this.device.confirmPassword &&
        !!this.device.custServiceId

      if (isValid) {
        this.handleAddDevice()
      } else {
        this.containsError = true
        this.$refs.errors.scrollIntoView()
      }
    },
    validateInputsUdp() {
      this.containsError = false

      const isValid =
        !!this.device.custDeviceName &&
        !!this.device.deviceId &&
        !!this.device.custServiceId &&
        !!this.device.hwDeviceId

      if (isValid) {
        this.handleAddDevice()
      } else {
        this.containsError = true
        this.$refs.errors.scrollIntoView()
      }
    },
    setErrors(errors) {
      this.$store.commit("modals/setWaitingLoader", "")
      this.errorList = errors
      this.showErrorModal = true
    }
  }
}
</script>
